import React, { useState } from "react";
import { Button, Box, Snackbar, Alert } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { clearResults } from "../../../../../features/OrgAdmin/orgAdminSlice";
import axios from "axios";
import md5 from "md5"; // ✅ MD5 import
import * as XLSX from "xlsx";
import FileSaver from "file-saver";

function ActionButtons() {
  const dispatch = useDispatch();
  const { formData, basicFormData, externalInfo, selectedOrg } = useSelector(
    (state) => state.org
  );
  const token = localStorage.getItem("token");

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const isUpdateMode = !!selectedOrg;

  const handleClear = () => {
    dispatch(clearResults());
  };

  const downLoadVehiclList = async () => {
    if (!selectedOrg?._id) {
      setSnackbar({
        open: true,
        message: "Please select an organization first!",
        severity: "warning",
      });
      return;
    }

    try {
      console.log("🟢 API call starting...");

      const response = await axios.get(
        `https://v3.dhundhoo.com/superAdmin/vendor/thing/vehicledevicedetails/${selectedOrg._id}`,
        {
          headers: { "x-dhundhoo-session": token },
        }
      );

      console.log("✅ API success:", response.data);

      const jsonData = response.data;

      console.log("🟢 Before Object.entries");
      const dataArray = Object.entries(jsonData).map(([vehicle, imei]) => ({
        VehicleNumber: vehicle,
        IMEI: imei,
      }));
      console.log("✅ dataArray created:", dataArray);

      const worksheet = XLSX.utils.json_to_sheet(dataArray);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Vehicles");

      console.log("🟢 Before XLSX.write");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

      console.log("🟢 Before Blob");
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
      const fileName = `${selectedOrg.orgInfo.orgName}_vehicles_lists.xlsx`;

      console.log("🟢 Before saveAs");
      FileSaver.saveAs(blob, fileName);

      setSnackbar({
        open: true,
        message: "Vehicle list downloaded successfully!",
        severity: "success",
      });

      console.log("✅ Done!");
    } catch (error) {
      console.error("❌ Error occurred in:", error);
      if (error.response) {
        console.log("📩 Server responded with:", error.response.status, error.response.data);
      } else if (error.request) {
        console.log("📡 No response received:", error.request);
      } else {
        console.log("⚙️ Error setting up request:", error.message);
      }

      setSnackbar({
        open: true,
        message: "Failed to download vehicle list!",
        severity: "error",
      });
    }
  };

  const downLoadVehiclLinks = async () => {
    if (!selectedOrg?._id) {
      setSnackbar({
        open: true,
        message: "Please select an organization first!",
        severity: "warning",
      });
      return;
    }

    try {
      console.log("🟢 API call starting...");

      const response = await axios.get(
        `https://v3.dhundhoo.com/superAdmin/vendor/thing/links/${selectedOrg._id}`,
        {
          headers: { "x-dhundhoo-session": token },
        }
      );

      console.log("✅ API success:", response.data);

      const jsonData = response.data;

      console.log("🟢 Before Object.entries");
      const dataArray = Object.entries(jsonData).map(([vehicle, link]) => ({
        VehicleNumber: vehicle,
        WebViewLink: link,
      }));
      console.log("✅ dataArray created:", dataArray);

      const worksheet = XLSX.utils.json_to_sheet(dataArray);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Vehicles");

      console.log("🟢 Before XLSX.write");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

      console.log("🟢 Before Blob");
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
      const fileName = `${selectedOrg.orgInfo.orgName}_WebViewLinks.xlsx`;

      console.log("🟢 Before saveAs");
      FileSaver.saveAs(blob, fileName);

      setSnackbar({
        open: true,
        message: "Vehicle link downloaded successfully!",
        severity: "success",
      });

      console.log("✅ Done!");
    } catch (error) {
      console.error("❌ Error occurred in:", error);
      if (error.response) {
        console.log("📩 Server responded with:", error.response.status, error.response.data);
      } else if (error.request) {
        console.log("📡 No response received:", error.request);
      } else {
        console.log("⚙️ Error setting up request:", error.message);
      }

      setSnackbar({
        open: true,
        message: "Failed to download vehicle link!",
        severity: "error",
      });
    }
  };


  const handleCreateOrg = async () => {
    const formattedExternalInfo = externalInfo.reduce((acc, item) => {
      if (item.key && item.value) {
        acc[item.key.toLowerCase()] = item.value;
      }
      return acc;
    }, {});

    // ✅ Encode password using MD5
    const encodedPassword = basicFormData.password
      ? md5(basicFormData.password)
      : "";

    // ✅ Common payload for both create/update
    const payload = {
      orgName: basicFormData.orgName,
      address: basicFormData.orgAddress,
      apiKey: formData.webApiKey,
      avatar: basicFormData.avatar,
      config: {
        wallet: formData.paymentWallet === "enable" ? "ENABLED" : "DISABLED",
        immobilization: "ENABLED",
        push_option:
          formData.smsFeature === "pushOnly"
            ? "PUSH_ONLY"
            : formData.smsFeature === "optimized"
              ? "OPTIMIZED"
              : formData.smsFeature === "sms"
                ? "SMS"
                : "DISABLED",
        sms_option:
          formData.dhundhooLink === "dontSend"
            ? "SMS_NO_LINK"
            : "SMS_WITH_LINK",
        attendance_option:
          formData.attendance === "enable" ? "ENABLED" : "DISABLED",
        odometer:
          formData.odometer === "enable" ? "ENABLED" : "DISABLED",
        logs_option:
          formData.logs === "enable" ? "ENABLED" : "DISABLED",
        management:
          formData.management === "enable" ? "ENABLED" : "DISABLED",
        routeOptimization:
          formData.routeOptimization === "enable" ? "ENABLED" : "DISABLED",
        alert: formData.alerts === "enable" ? "ENABLED" : "DISABLED",
        planning: formData.planning === "enable" ? "ENABLED" : "DISABLED",
        trip: formData.tripDetails === "enable" ? "ENABLED" : "DISABLED",
        jobRole: formData.jobRole === "enable" ? "ENABLED" : "DISABLED",
        fuelLog: formData.fuelLog === "enable" ? "ENABLED" : "DISABLED",
        liveCamera: formData.liveCamera === "enable" ? "ENABLED" : "DISABLED",
        alertEmailIds: basicFormData.alertEmailIds.join(",") || "",
        vendor_option:
          formData.vendorConfigurations === "vendorOnly"
            ? "VENDOR_ONLY"
            : formData.vendorConfigurations === "dhundhooVendor"
              ? "DHUNDHOO_VENDOR"
              : formData.vendorConfigurations === "vendorPlus"
                ? "ENABLE_VENDOR_PLUS"
                : "NO_VENDOR",
      },
      disableJourneys: formData.journeyStatus === "disabled",
      emailId: basicFormData.emailId,
      externalInfo: formattedExternalInfo,
      orgCode: basicFormData.orgShortCode,
      orgHandle: isUpdateMode ? selectedOrg.orgInfo.orgHandle : "",
      phoneNumber: basicFormData.phoneNumber,
      speedLimit: formData.speedLimit / 3.6,
      licenseCount: formData.licenseCount,
      type: "ORG_SCHOOL",
      vendorHandle: formData.vendorHandle,
      encodedPassword, // Always included
    };

    // ✅ Add `signUp` ONLY when creating new org
    if (!isUpdateMode) {
      payload.signUp = {
        firstName: basicFormData.firstName || "",
        lastName: basicFormData.lastName || "",
        // phoneNumber: basicFormData.phoneNumber,
        phoneNumber: basicFormData.phoneNumber ? `+91${basicFormData.phoneNumber}` : "",
        emailId: basicFormData.emailId,
        encodedPassword,
        installToken: crypto.randomUUID(), // ✅ generates unique token
        mode: "EMAIL",
      };
    }

    try {
      const url = "https://v3.dhundhoo.com/auth/upsertOrg";
      const response = await axios.post(url, payload, {
        headers: { "x-dhundhoo-session": token },
      });

      setSnackbar({
        open: true,
        message: isUpdateMode
          ? "Organization updated successfully!"
          : "Organization created successfully!",
        severity: "success",
      });

      console.log("Success:", response.data);
      handleClear();
    } catch (error) {
      console.error("Error:", error);
      setSnackbar({
        open: true,
        message: isUpdateMode
          ? "Failed to update organization!"
          : "Failed to create organization!",
        severity: "error",
      });
    }
  };

  const handleRemoveOrg = async () => {
    const formattedExternalInfo = externalInfo.reduce((acc, item) => {
      if (item.key && item.value) {
        acc[item.key.toLowerCase()] = item.value;
      }
      return acc;
    }, {});

    // ✅ Encode password using MD5
    const encodedPassword = basicFormData.password
      ? md5(basicFormData.password)
      : "";

    // ✅ Common payload for both create/update
    const payload = {
      orgName: basicFormData.orgName,
      address: basicFormData.orgAddress,
      apiKey: formData.webApiKey,
      avatar: basicFormData.avatar,
      config: {
        wallet: formData.paymentWallet === "enable" ? "ENABLED" : "DISABLED",
        immobilization: "ENABLED",
        push_option:
          formData.smsFeature === "pushOnly"
            ? "PUSH_ONLY"
            : formData.smsFeature === "optimized"
              ? "OPTIMIZED"
              : formData.smsFeature === "sms"
                ? "SMS"
                : "DISABLED",
        sms_option:
          formData.dhundhooLink === "dontSend"
            ? "SMS_NO_LINK"
            : "SMS_WITH_LINK",
        attendance_option:
          formData.attendance === "enable" ? "ENABLED" : "DISABLED",
        odometer:
          formData.odometer === "enable" ? "ENABLED" : "DISABLED",
        logs_option:
          formData.logs === "enable" ? "ENABLED" : "DISABLED",
        management:
          formData.management === "enable" ? "ENABLED" : "DISABLED",
        routeOptimization:
          formData.routeOptimization === "enable" ? "ENABLED" : "DISABLED",
        alert: formData.alerts === "enable" ? "ENABLED" : "DISABLED",
        planning: formData.planning === "enable" ? "ENABLED" : "DISABLED",
        trip: formData.tripDetails === "enable" ? "ENABLED" : "DISABLED",
        jobRole: formData.jobRole === "enable" ? "ENABLED" : "DISABLED",
        fuelLog: formData.fuelLog === "enable" ? "ENABLED" : "DISABLED",
        liveCamera: formData.liveCamera === "enable" ? "ENABLED" : "DISABLED",
        alertEmailIds: basicFormData.alertEmailIds.join(",") || "",
        vendor_option:
          formData.vendorConfigurations === "vendorOnly"
            ? "VENDOR_ONLY"
            : formData.vendorConfigurations === "dhundhooVendor"
              ? "DHUNDHOO_VENDOR"
              : formData.vendorConfigurations === "vendorPlus"
                ? "ENABLE_VENDOR_PLUS"
                : "NO_VENDOR",
        archived: true,
        orgType: formData.orgType,
      },
      disableJourneys: formData.journeyStatus === "disabled",
      emailId: basicFormData.emailId,
      externalInfo: formattedExternalInfo,
      orgCode: basicFormData.orgShortCode,
      orgHandle: isUpdateMode ? selectedOrg.orgInfo.orgHandle : "",
      phoneNumber: basicFormData.phoneNumber,
      speedLimit: formData.speedLimit / 3.6,
      type: "ORG_SCHOOL",
      vendorHandle: formData.vendorHandle,
      encodedPassword, // Always included
    };

    // ✅ Add `signUp` ONLY when creating new org
    if (!isUpdateMode) {
      payload.signUp = {
        firstName: basicFormData.firstName || "",
        lastName: basicFormData.lastName || "",
        // phoneNumber: basicFormData.phoneNumber,
        phoneNumber: basicFormData.phoneNumber ? `+91${basicFormData.phoneNumber}` : "",
        emailId: basicFormData.emailId,
        encodedPassword,
        installToken: crypto.randomUUID(), // ✅ generates unique token
        mode: "EMAIL",
      };
    }

    try {
      const url = "https://v3.dhundhoo.com/auth/upsertOrg";
      const response = await axios.post(url, payload, {
        headers: { "x-dhundhoo-session": token },
      });

      setSnackbar({
        open: true,
        message: isUpdateMode
          ? "Organization updated successfully!"
          : "Organization created successfully!",
        severity: "success",
      });

      console.log("Success:", response.data);
      handleClear();
    } catch (error) {
      console.error("Error:", error);
      setSnackbar({
        open: true,
        message: isUpdateMode
          ? "Failed to update organization!"
          : "Failed to create organization!",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          mt: 3,
          flexWrap: "wrap",
        }}
      >


        <Button
          variant="contained"
          color="success"
          onClick={handleCreateOrg}
          disabled={!basicFormData.orgName || !basicFormData.emailId}
          sx={{ minWidth: 140, color: "white" }}
        >
          {isUpdateMode ? "Update Org" : "Create Org"}
        </Button>

        <Button
          variant="outlined"
          onClick={handleClear}
          sx={{ minWidth: 120 }}
          style={{ color: "black" }}
        >
          Clear
        </Button>

        {isUpdateMode && (
          <Button
            variant="contained"
            color="info"
            onClick={downLoadVehiclLinks}
            sx={{ minWidth: 140, color: "white" }}
          >
            Download Links
          </Button>
        )}

        {isUpdateMode && (
          <Button
            variant="contained"
            color="info"
            onClick={downLoadVehiclList}
            sx={{ minWidth: 140, color: "white" }}
          >
            Vehicle List
          </Button>
        )}

        {isUpdateMode && (
          <Button
            variant="contained"
            color="error"
            onClick={handleRemoveOrg}
            sx={{ minWidth: 140, color: "white" }}
          >
            Remove Org
          </Button>
        )}

      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ActionButtons;
